// Browser-side ComfyUI client.
//
// In production the cockpit calls /api/comfy/* (Pages Function proxy);
// the proxy talks to the in-cluster service URL. We never expose the
// raw ComfyUI endpoint to the browser — credentials + queue rate
// limits live behind our gate.

import { FLUX_MODEL, FLUX_NEGATIVE_BASE, FLUX_PHOTOGRAPHIC_BASE, type FluxRoomCue, type FluxSize, type FluxStyle } from "../../data/generated/flux-prompts.generated";

export interface RenderRequest {
  style: FluxStyle;
  roomCue: FluxRoomCue | null;
  size: FluxSize;
  /** Free-text additions from the designer (e.g. specific SKUs). */
  customPrompt?: string;
  /** Optional negative additions. */
  customNegative?: string;
  /** Deterministic seed; omit to randomise. */
  seed?: number;
}

export interface RenderTicket {
  prompt_id: string;
  client_id: string;
  submitted_at: string;
}

export interface RenderResult {
  prompt_id: string;
  status: "queued" | "running" | "done" | "error";
  image_urls: readonly string[];
  error?: string;
  elapsed_ms?: number;
}

export function composeFluxPrompt(req: RenderRequest): { positive: string; negative: string } {
  // Order matters for FLUX: the first tokens carry the most semantic
  // weight. We lead with the customPrompt (the graph-grounded
  // fragment) so Intent/Materials/SKUs dominate the diffusion, then
  // style base, then room cue, then photographic conventions last as
  // a quality tail. No fragment is duplicated.
  const positive = [
    req.customPrompt?.trim() ?? "",
    req.style.base,
    req.roomCue?.cue ?? "",
    FLUX_PHOTOGRAPHIC_BASE,
  ].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const negative = [
    FLUX_NEGATIVE_BASE,
    req.customNegative?.trim() ?? "",
  ].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return { positive, negative };
}

/** Build a human-readable error from a non-ok ComfyUI proxy response.
 *  FastAPI returns `{"detail": "..."}`; Cloudflare returns an HTML
 *  error page when the tunnel can't reach the upstream. CLAUDE.md §5 —
 *  surface the real reason, never dump raw HTML into the toast. */
async function explainComfyError(r: Response, label: string): Promise<Error> {
  let detail = "";
  let isHtml = false;
  try {
    const body = await r.text();
    const trimmed = body.trim();
    if (trimmed.length > 0) {
      const lowerStart = trimmed.slice(0, 32).toLowerCase();
      if (lowerStart.startsWith("<!doctype html") || lowerStart.startsWith("<html") || (r.headers.get("content-type") ?? "").includes("text/html")) {
        // HTML error page from Cloudflare / a reverse proxy. Don't
        // bleed markup into the toast — the status code is what matters.
        isHtml = true;
      } else {
        try {
          const parsed = JSON.parse(trimmed) as { detail?: unknown; error?: unknown; message?: unknown };
          if (typeof parsed.detail === "string") detail = parsed.detail;
          else if (typeof parsed.error === "string") detail = parsed.error;
          else if (typeof parsed.message === "string") detail = parsed.message;
        } catch {
          detail = trimmed.length > 160 ? trimmed.slice(0, 160) + "..." : trimmed;
        }
      }
    }
  } catch { /* ignore body read failure */ }
  // 502/503/504 — the Cloudflare Tunnel to ComfyUI
  // (comfy.hypergraphmind.com) isn't bound yet.
  if (r.status === 502 || r.status === 503 || r.status === 504) {
    return new Error(`Render service unavailable (HTTP ${r.status}). The prompt was composed and grounded; renders return once the Cloudflare Tunnel to ComfyUI (X-LLM-Model: comfy-flux-dev via APISIX) is wired.`);
  }
  if (r.status === 500) {
    // HTML body == gateway returned its default error page → the upstream
    // isn't even reachable from the Pages Function. Same root cause as
    // 502/503; phrase it that way honestly instead of saying "500".
    if (isHtml) {
      return new Error(`Render service unreachable (gateway 500 · HTML error page). The Pages Function couldn't reach ComfyUI — the comfy-flux-dev APISIX route is unbound or the Cloudflare Tunnel to comfy.hypergraphmind.com is down.`);
    }
    return new Error(`ComfyUI proxy returned 500${detail ? ` (${detail})` : ""}. The pipeline reached the gateway but the upstream rejected the job — likely the comfy-flux-dev route isn't configured or the worker is offline.`);
  }
  if (r.status === 404) {
    return new Error(`Render endpoint not found (HTTP 404). The /api/comfy/render Pages Function ships with the build; if you see this in production, the agent's comfy-render endpoint isn't deployed yet.`);
  }
  if (r.status === 401 || r.status === 403) {
    return new Error(`Render not authorised (HTTP ${r.status})${detail ? ` — ${detail}` : ""}. Sign back in and retry.`);
  }
  return new Error(`${label}: HTTP ${r.status}${detail && !isHtml ? ` — ${detail}` : ""}`);
}

export async function submitRender(req: RenderRequest): Promise<RenderTicket> {
  const r = await fetch("/api/comfy/render", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      style_id: req.style.id,
      room_kind: req.roomCue?.kind ?? null,
      size_id: req.size.id,
      customPrompt: req.customPrompt ?? "",
      customNegative: req.customNegative ?? "",
      seed: req.seed ?? null,
      model: FLUX_MODEL,
    }),
  });
  if (!r.ok) throw await explainComfyError(r, "render submit");
  return r.json() as Promise<RenderTicket>;
}

export async function pollRender(prompt_id: string): Promise<RenderResult> {
  const r = await fetch(`/api/comfy/render/${encodeURIComponent(prompt_id)}`);
  if (!r.ok) throw await explainComfyError(r, "render poll");
  return r.json() as Promise<RenderResult>;
}

/**
 * Convenience: submit + poll until done.
 * Production swap: subscribe to ws://.../ws?clientId=… for live progress.
 */
export async function renderAndWait(req: RenderRequest, opts: { timeoutMs?: number; intervalMs?: number } = {}): Promise<RenderResult> {
  const timeout = opts.timeoutMs ?? 90_000;
  const interval = opts.intervalMs ?? 1_500;
  const t0 = Date.now();
  const ticket = await submitRender(req);
  while (Date.now() - t0 < timeout) {
    const r = await pollRender(ticket.prompt_id);
    if (r.status === "done" || r.status === "error") return r;
    await new Promise((res) => setTimeout(res, interval));
  }
  return { prompt_id: ticket.prompt_id, status: "error", image_urls: [], error: "timeout" };
}
